import React, { useState, useEffect, useRef } from 'react';
import api from '../../api';
import ImagePreview from './common/ImagePreview';
import { FiTrash2, FiUpload, FiEdit2, FiPlus, FiSave, FiX, FiUser, FiAlertCircle, FiCheckCircle, FiCamera, FiLink, FiBook, FiAward } from 'react-icons/fi';

// Professional Modal Component
const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-slate-900/40 backdrop-blur-xs" onClick={onClose}></div>
        
        <div className={`relative inline-block w-full ${sizeClasses[size]} p-5 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-2xl border border-slate-100 shadow-xl sm:my-8 sm:align-middle`}>
          <div className="absolute top-4 right-4 z-10">
            <button
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
          
          <div className="w-full">
            <h3 className="text-base font-bold text-slate-900 pb-3 border-b border-slate-100">
              {title}
            </h3>
            <div className="mt-4">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Confirmation Modal
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Delete", cancelText = "Cancel", type = "danger" }) => {
  const buttonClasses = {
    danger: "bg-rose-600 hover:bg-rose-700 focus:ring-rose-500",
    warning: "bg-amber-600 hover:bg-amber-700 focus:ring-amber-500",
    primary: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <div className="flex flex-col items-center text-center sm:text-left sm:flex-row sm:items-start gap-4">
        <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 mx-auto bg-rose-50 text-rose-600 rounded-xl border border-rose-100 sm:mx-0">
          <FiAlertCircle className="w-5 h-5" />
        </div>
        <div className="flex-1 space-y-1.5">
          <h4 className="text-sm font-bold text-slate-900">{title}</h4>
          <p className="text-xs text-slate-500 leading-relaxed">{message}</p>
        </div>
      </div>
      
      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end border-t border-slate-100 pt-4">
        <button
          type="button"
          className="w-full sm:w-auto px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          onClick={onClose}
        >
          {cancelText}
        </button>
        <button
          type="button"
          className={`w-full sm:w-auto px-4 py-2 text-xs font-semibold text-white rounded-lg shadow-xs transition-all focus:ring-2 focus:ring-offset-1 focus:outline-hidden ${buttonClasses[type]}`}
          onClick={onConfirm}
        >
          {confirmText}
        </button>
      </div>
    </Modal>
  );
};

// Notification Component
const Notification = ({ message, type = 'success', onClose }) => {
  const bgColor = {
    success: 'bg-emerald-50/95 border-emerald-200 text-emerald-900',
    error: 'bg-rose-50/95 border-rose-200 text-rose-900',
    warning: 'bg-amber-50/95 border-amber-200 text-amber-900',
    info: 'bg-blue-50/95 border-blue-200 text-blue-900'
  };

  const iconColor = {
    success: 'text-emerald-500',
    error: 'text-rose-500',
    warning: 'text-amber-500',
    info: 'text-blue-500'
  };

  return (
    <div className={`fixed bottom-4 right-4 left-4 sm:left-auto z-50 max-w-sm rounded-xl p-4 shadow-md border backdrop-blur-xs flex items-start gap-3 transition-all ${bgColor[type]}`} style={{ animation: 'statusSlideUp 0.25s ease-out forwards' }}>
      <div className="flex-shrink-0 mt-0.5">
        <FiCheckCircle className={`w-4 h-4 ${iconColor[type]}`} />
      </div>
      <div className="flex-1">
        <p className="text-xs font-medium leading-relaxed">
          {message}
        </p>
      </div>
      <div className="flex-shrink-0">
        <button
          className="p-0.5 rounded-md opacity-70 hover:opacity-100 transition-opacity focus:outline-hidden"
          onClick={onClose}
        >
          <FiX className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

// Enhanced Faculty Form Component with Photo Upload
const FacultyForm = ({ faculty, onSave, onCancel, isEditing, isLoading }) => {
  const [formData, setFormData] = useState({
    firstName: faculty?.firstName || '',
    lastName: faculty?.lastName || '',
    designation: faculty?.designation || '',
    department: faculty?.department || '',
    experience: faculty?.experience || '',
    linkedinId: faculty?.linkedinId || '',
    googleScholarId: faculty?.googleScholarId || '',
    orcidId: faculty?.orcidId || '',
    bio: faculty?.bio || '',
    email: faculty?.email || '',
    phone: faculty?.phone || '',
    order: typeof faculty?.order !== 'undefined' && faculty?.order !== null ? faculty.order : ''
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(faculty?.photo || faculty?.image || '');
  const fileInputRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const payload = { ...formData };
    if (payload.order === '' || payload.order === null || typeof payload.order === 'undefined') {
      payload.order = null;
    } else {
      const n = Number(payload.order);
      payload.order = Number.isFinite(n) && !Number.isNaN(n) ? Math.max(1, Math.floor(n)) : null;
    }

    onSave(payload, photoFile);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, WebP)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    setPhotoFile(file);
    
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPhotoPreview(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 max-h-[70vh] overflow-y-auto px-1 no-scrollbar">
      {/* Photo Upload Section */}
      <div className="flex flex-col items-center space-y-2 py-2">
        <div className="relative">
          <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-slate-200 shadow-xs bg-slate-50 flex items-center justify-center">
            {photoPreview ? (
              <img
                src={photoPreview}
                alt="Faculty preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <FiUser className="w-8 h-8 text-slate-400" />
            )}
          </div>
          <div className="absolute -bottom-1 -right-1 flex gap-1">
            <button
              type="button"
              onClick={triggerFileInput}
              className="p-1.5 bg-blue-600 text-white rounded-full shadow-md hover:bg-blue-700 transition-colors"
              aria-label="Upload photo"
            >
              <FiCamera className="w-3.5 h-3.5" />
            </button>
            {photoPreview && (
              <button
                type="button"
                onClick={removePhoto}
                className="p-1.5 bg-rose-600 text-white rounded-full shadow-md hover:bg-rose-700 transition-colors"
              >
                <FiX className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className="hidden"
          />
        </div>
        <p className="text-[11px] text-slate-400 text-center">
          Recommended: Square ratio aspect, max 5MB size limit.
        </p>
      </div>

      {/* Personal Information Section */}
      <div className="bg-slate-50/60 p-4 rounded-xl border border-slate-200">
        <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
          <FiUser className="w-4 h-4 text-blue-500" />
          Personal Identity Info
        </h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="firstName" className="block text-xs font-semibold text-slate-600 mb-1">
              First Name *
            </label>
            <input
              type="text"
              id="firstName"
              required
              value={formData.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              className="block w-full text-xs px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              placeholder="Enter first name"
            />
          </div>

          <div>
            <label htmlFor="lastName" className="block text-xs font-semibold text-slate-600 mb-1">
              Last Name *
            </label>
            <input
              type="text"
              id="lastName"
              required
              value={formData.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
              className="block w-full text-xs px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              placeholder="Enter last name"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-xs font-semibold text-slate-600 mb-1">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className="block w-full text-xs px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              placeholder="faculty@college.com"
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-xs font-semibold text-slate-600 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              className="block w-full text-xs px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              placeholder="+1 (555) 123-4567"
            />
          </div>
        </div>
      </div>

      {/* Professional Information Section */}
      <div className="bg-slate-50/60 p-4 rounded-xl border border-slate-200">
        <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
          <FiAward className="w-4 h-4 text-blue-500" />
          Professional Credentials
        </h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="designation" className="block text-xs font-semibold text-slate-600 mb-1">
              Designation Title *
            </label>
            <select
              id="designation"
              required
              value={formData.designation}
              onChange={(e) => handleChange('designation', e.target.value)}
              className="block w-full text-xs px-3 py-2 border border-slate-200 bg-white rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            >
              <option value="">Select designation title</option>
              <option value="Professor">Professor</option>
              <option value="Associate Professor">Associate Professor</option>
              <option value="Assistant Professor">Assistant Professor</option>
              <option value="Lecturer">Lecturer</option>
              <option value="Adjunct Professor">Adjunct Professor</option>
              <option value="Visiting Professor">Visiting Professor</option>
              <option value="Dean">Dean</option>
              <option value="Department Head">Department Head</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label htmlFor="department" className="block text-xs font-semibold text-slate-600 mb-1">
              Department Branch
            </label>
            <input
              type="text"
              id="department"
              value={formData.department}
              onChange={(e) => handleChange('department', e.target.value)}
              className="block w-full text-xs px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              placeholder="Computer Applications"
            />
          </div>

          <div>
            <label htmlFor="order" className="block text-xs font-semibold text-slate-600 mb-1">
              Priority Ranking (1 = highest)
            </label>
            <input
              id="order"
              type="number"
              min={1}
              value={formData.order ?? ''}
              onChange={(e) => handleChange('order', e.target.value)}
              placeholder="Automatic ordering baseline grid"
              className="block w-full text-xs px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>

          <div>
            <label htmlFor="experience" className="block text-xs font-semibold text-slate-600 mb-1">
              Experience Span
            </label>
            <select
              id="experience"
              value={formData.experience}
              onChange={(e) => handleChange('experience', e.target.value)}
              className="block w-full text-xs px-3 py-2 border border-slate-200 bg-white rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            >
              <option value="">Select experience range</option>
              <option value="0-2 years">0-2 years</option>
              <option value="2-5 years">2-5 years</option>
              <option value="5-10 years">5-10 years</option>
              <option value="10-15 years">10-15 years</option>
              <option value="15-20 years">15-20 years</option>
              <option value="20+ years">20+ years</option>
            </select>
          </div>
        </div>
      </div>

      {/* Professional Profiles Section */}
      <div className="bg-slate-50/60 p-4 rounded-xl border border-slate-200">
        <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
          <FiLink className="w-4 h-4 text-blue-500" />
          Research & Social Indexes
        </h4>
        <div className="space-y-3">
          <div>
            <label htmlFor="linkedinId" className="block text-xs font-semibold text-slate-600 mb-1">
              LinkedIn Profile Slug
            </label>
            <div className="flex rounded-lg shadow-xs overflow-hidden border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-blue-500">
              <span className="inline-flex items-center px-3 border-r border-slate-150 bg-slate-50 text-slate-400 text-xs select-none">
                linkedin.com/in/
              </span>
              <input
                type="text"
                id="linkedinId"
                value={formData.linkedinId}
                onChange={(e) => handleChange('linkedinId', e.target.value)}
                className="flex-1 min-w-0 block w-full text-xs px-3 py-2 border-0 bg-transparent focus:ring-0 focus:outline-hidden text-slate-700"
                placeholder="username"
              />
            </div>
          </div>

          <div>
            <label htmlFor="googleScholarId" className="block text-xs font-semibold text-slate-600 mb-1">
              Google Scholar Identifier Code
            </label>
            <input
              type="text"
              id="googleScholarId"
              value={formData.googleScholarId}
              onChange={(e) => handleChange('googleScholarId', e.target.value)}
              className="block w-full text-xs px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              placeholder="Enter Google Scholar profile unique key"
            />
          </div>

          <div>
            <label htmlFor="orcidId" className="block text-xs font-semibold text-slate-600 mb-1">
              ORCID Digital Identifier
            </label>
            <input
              type="text"
              id="orcidId"
              value={formData.orcidId}
              onChange={(e) => handleChange('orcidId', e.target.value)}
              className="block w-full text-xs px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              placeholder="0000-0000-0000-0000"
              pattern="\d{4}-\d{4}-\d{4}-\d{3}[\dX]"
            />
            <p className="mt-1 text-[10px] text-slate-400">Standard mapping signature format: XXXX-XXXX-XXXX-XXXX</p>
          </div>
        </div>
      </div>

      {/* Biography Section */}
      <div className="bg-slate-50/60 p-4 rounded-xl border border-slate-200">
        <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
          <FiBook className="w-4 h-4 text-blue-500" />
          Biography Summary
        </h4>
        <div>
          <label htmlFor="bio" className="block text-xs font-semibold text-slate-600 mb-1">
            Professional Bio Notes
          </label>
          <textarea
            id="bio"
            rows={3}
            value={formData.bio}
            onChange={(e) => handleChange('bio', e.target.value)}
            className="block w-full text-xs px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
            placeholder="Brief profile logs highlighting academic achievements..."
          />
          <p className="mt-1 text-[10px] text-slate-400">
            Optimal presentation density index: 150-300 standard data tokens.
          </p>
        </div>
      </div>

      {/* Form Actions footer line */}
      <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-blue-600 rounded-lg shadow-xs hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Processing...</span>
            </>
          ) : (
            <>
              <FiSave className="w-3.5 h-3.5" />
              <span>{isEditing ? 'Update Profile' : 'Confirm Registration'}</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
};

const FacultySection = ({ websiteData, handleInputChange, handleSave, tenantId, token }) => {
  const [localFaculty, setLocalFaculty] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showFacultyForm, setShowFacultyForm] = useState(false);
  const [editingFaculty, setEditingFaculty] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [facultyToDelete, setFacultyToDelete] = useState(null);
  const [notification, setNotification] = useState(null);

  // Apply auth token to api instance
  useEffect(() => {
    if (!api || typeof api !== 'object') return;
    if (token) {
      api.defaults = api.defaults || {};
      api.defaults.headers = api.defaults.headers || {};
      api.defaults.headers.common = api.defaults.headers.common || {};
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else if (api.defaults?.headers?.common) {
      delete api.defaults.headers.common['Authorization'];
    }
    return () => {
      if (api.defaults?.headers?.common) delete api.defaults.headers.common['Authorization'];
    };
  }, [token]);

  // Initialize local faculty state
  useEffect(() => {
    setLocalFaculty(Array.isArray(websiteData.faculty) ? websiteData.faculty : []);
  }, [websiteData.faculty]);

  // Fetch faculty data
  useEffect(() => {
    if (!tenantId) return;
    let mounted = true;
    const fetchFaculty = async () => {
      try {
        setIsLoading(true);
        const resp = await api.get(`/api/website/${tenantId}/faculty`);
        if (!mounted) return;
        if (resp?.data?.success) {
          const members = resp.data.data || [];
          const normalized = members.map(m => ({
            ...m,
            name: m.name || `${m.firstName || ''} ${m.lastName || ''}`.trim(),
            photo: m.photo || m.image || '',
            fileId: m.image || m.fileId || null
          }));
          setLocalFaculty(normalized);
        } else {
          showNotification('Failed to load faculty members', 'error');
        }
      } catch (_) {
        showNotification('Failed to load faculty members', 'error');
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    fetchFaculty();
    return () => { mounted = false; };
  }, [tenantId]);

  // Notification handler
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Faculty form handlers
  const handleCreateFaculty = () => {
    setEditingFaculty(null);
    setShowFacultyForm(true);
  };

  const handleEditFaculty = (faculty) => {
    setEditingFaculty(faculty);
    setShowFacultyForm(true);
  };

  // Save faculty handler
  const handleSaveFaculty = async (formData, photoFile) => {
    if (!tenantId) {
      showNotification('Tenant ID is required', 'error');
      return;
    }

    setIsLoading(true);
    try {
      let response;
      let facultyId;

      if (editingFaculty) {
        response = await api.put(`/api/website/${tenantId}/faculty/${editingFaculty._id}`, formData);
      } else {
        response = await api.post(`/api/website/${tenantId}/faculty`, formData);
      }

      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'Failed to save faculty member');
      }

      facultyId = response.data.data?._id || editingFaculty?._id;

      if (photoFile && facultyId) {
        const photoFormData = new FormData();
        photoFormData.append('file', photoFile);
        photoFormData.append('section', 'faculty');
        photoFormData.append('subField', 'photo');

        try {
          const photoResponse = await api.post(
            `/api/website/${tenantId}/upload/faculty`,
            photoFormData,
            {
              headers: { 'Content-Type': 'multipart/form-data' }
            }
          );

          if (photoResponse?.data?.success) {
            const photoUrl = photoResponse.data.data.url;
            
            const updateResponse = await api.put(
              `/api/website/${tenantId}/faculty/${facultyId}`,
              { photo: photoUrl }
            );
            
            if (updateResponse?.data?.success) {
              showNotification('Faculty member and photo saved successfully');
            } else {
              showNotification('Photo uploaded but failed to update faculty', 'warning');
            }
          } else {
            showNotification('Faculty saved, but photo upload failed', 'warning');
          }
        } catch (photoError) {
          console.error('Photo upload error:', photoError);
          showNotification('Faculty saved, but photo upload failed', 'warning');
        }
      } else {
        showNotification(
          editingFaculty ? 'Faculty member updated successfully' : 'Faculty member created successfully'
        );
      }

      const listResp = await api.get(`/api/website/${tenantId}/faculty`);
      if (listResp?.data?.success) {
        const members = (listResp.data.data || []).map(m => ({
          ...m,
          name: m.name || `${m.firstName || ''} ${m.lastName || ''}`.trim(),
          photo: m.photo || m.image || ''
        }));
        setLocalFaculty(members);
        handleInputChange('faculty', null, members);
      }
      
      setShowFacultyForm(false);
    } catch (err) {
      console.error('Save faculty error:', err);
      showNotification(err.message || 'Failed to save faculty member', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Delete faculty handler
  const handleDeleteFaculty = async () => {
    if (!facultyToDelete) return;

    setIsLoading(true);
    try {
      const response = await api.delete(`/api/website/${tenantId}/faculty/${facultyToDelete._id}`);
      if (response?.data?.success) {
        setLocalFaculty(prev => prev.filter(f => f._id !== facultyToDelete._id));
        showNotification('Faculty member deleted successfully');
      } else {
        throw new Error(response?.data?.message || 'Failed to delete faculty member');
      }
    } catch (err) {
      showNotification(err.message || 'Failed to delete faculty member', 'error');
    } finally {
      setIsLoading(false);
      setShowDeleteModal(false);
      setFacultyToDelete(null);
    }
  };

  const confirmDeleteFaculty = (faculty) => {
    setFacultyToDelete(faculty);
    setShowDeleteModal(true);
  };

  // Photo upload for existing faculty using regular endpoint
  const handlePhotoUpload = async (e, facultyId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = '';

    const faculty = localFaculty.find(f => f._id === facultyId);
    if (!faculty) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('section', 'faculty');
    formData.append('subField', 'photo');

    try {
      setIsLoading(true);
      
      const resp = await api.post(
        `/api/website/${tenantId}/upload/faculty`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      if (resp?.data?.success) {
        const photoUrl = resp.data.data.url;
        
        const updateResponse = await api.put(
          `/api/website/${tenantId}/faculty/${facultyId}`,
          { photo: photoUrl }
        );
        
        if (updateResponse?.data?.success) {
          setLocalFaculty(prev => prev.map(f => 
            f._id === facultyId ? { ...f, photo: photoUrl, image: photoUrl } : f
          ));
          showNotification('Faculty photo updated successfully');
        } else {
          showNotification('Photo uploaded but failed to update faculty', 'error');
        }
      } else {
        showNotification('Failed to upload faculty photo', 'error');
      }
    } catch (error) {
      console.error('Photo upload error:', error);
      showNotification('Failed to upload faculty photo', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-150 bg-white flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-900">Faculty Registry Database</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Manage academic instructors, research profiles, and directory mapping listings.
          </p>
        </div>
        <button
          onClick={handleCreateFaculty}
          disabled={isLoading || !tenantId}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-all shadow-xs disabled:opacity-50 disabled:cursor-not-allowed self-start sm:self-auto"
        >
          <FiPlus className="w-3.5 h-3.5" />
          <span>Add Staff Record</span>
        </button>
      </div>

      {/* Content Canvas */}
      <div className="p-5">
        {!tenantId && (
          <div className="p-4 mb-5 bg-amber-50 border border-amber-200 rounded-xl flex gap-3">
            <FiAlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <h3 className="text-xs font-bold text-amber-900">Tenant Workspace Map Missing</h3>
              <p className="mt-0.5 text-xs text-amber-700 leading-relaxed">Please load a valid parameter block inside your panel map layout routing.</p>
            </div>
          </div>
        )}

        {/* Loading Blank Grid Matrix */}
        {isLoading && localFaculty.length === 0 && (
          <div className="flex flex-col justify-center items-center py-16 space-y-3">
            <div className="animate-spin rounded-full h-8 w-8 border-3 border-blue-600 border-t-transparent"></div>
            <p className="text-xs font-medium text-slate-400 animate-pulse">Retrieving instructor rows...</p>
          </div>
        )}

        {/* Empty State Layout */}
        {!isLoading && localFaculty.length === 0 && (
          <div className="text-center py-12 border-2 border-dashed border-slate-200 bg-slate-50/50 rounded-2xl max-w-md mx-auto">
            <div className="h-10 w-10 bg-slate-100 border border-slate-200 text-slate-400 rounded-xl flex items-center justify-center mx-auto mb-3">
              <FiUser className="w-5 h-5" />
            </div>
            <h3 className="text-sm font-bold text-slate-800">No staff accounts found</h3>
            <p className="mt-1 text-xs text-slate-500 max-w-xs mx-auto leading-relaxed">
              Populate your academic workspace cluster map by logging your first profile account index.
            </p>
            <div className="mt-5">
              <button
                onClick={handleCreateFaculty}
                disabled={!tenantId}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiPlus className="w-3.5 h-3.5" />
                <span>Initialize Staff Profile</span>
              </button>
            </div>
          </div>
        )}

        {/* Grid Map Rows Display */}
        {!isLoading && localFaculty.length > 0 && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {localFaculty.map((faculty) => (
              <div key={faculty._id} className="relative flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs hover:border-slate-300 transition-all group">
                {typeof faculty.order !== 'undefined' && faculty.order !== null && (
                  <div className="absolute top-2.5 left-2.5 synchronized-index-badge z-20 px-2 py-0.5 text-[10px] font-bold text-white bg-blue-600/90 rounded-md backdrop-blur-xs shadow-xs">
                    Rank #{faculty.order}
                  </div>
                )}
                
                {/* Visual Header Profile Aspect ratio canvas block */}
                <div className="relative bg-slate-50 aspect-video overflow-hidden border-b border-slate-100">
                  <ImagePreview 
                    src={faculty.photo || faculty.image || ''} 
                    alt={faculty.name || 'Faculty block'}
                    className="object-cover w-full h-full transform group-hover:scale-101 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-900/40 opacity-0 group-hover:opacity-100 sm:transition-opacity">
                    <label className="cursor-pointer p-2 bg-white/95 text-slate-700 rounded-xl shadow-md hover:bg-white transition-all transform translate-y-1 group-hover:translate-y-0 duration-200">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handlePhotoUpload(e, faculty._id)}
                        disabled={isLoading}
                      />
                      <FiUpload className="w-4 h-4" />
                    </label>
                  </div>
                </div>

                {/* Information Card Block Body */}
                <div className="p-4 flex-1 flex flex-col justify-between space-y-3">
                  <div className="space-y-1">
                    <h3 className="text-sm font-bold text-slate-900 tracking-tight leading-snug">
                      {faculty.name || `${faculty.firstName || ''} ${faculty.lastName || ''}`.trim()}
                    </h3>
                    <p className="text-xs font-semibold text-blue-600">
                      {faculty.designation || faculty.position}
                    </p>
                    {faculty.department && (
                      <p className="text-[11px] text-slate-500 font-medium">
                        {faculty.department}
                      </p>
                    )}
                    {faculty.experience && (
                      <span className="inline-block text-[10px] font-medium px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-md text-slate-600">
                        {faculty.experience}
                      </span>
                    )}
                  </div>
                  
                  {faculty.bio && (
                    <p className="text-xs text-slate-500 leading-relaxed line-clamp-2 pt-1 border-t border-slate-100/60">
                      {faculty.bio}
                    </p>
                  )}
                </div>

                {/* Operations Line Footer Controls */}
                <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-150 flex items-center justify-between gap-2">
                  <button
                    onClick={() => handleEditFaculty(faculty)}
                    disabled={isLoading}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                  >
                    <FiEdit2 className="w-3.5 h-3.5 text-slate-400" />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => confirmDeleteFaculty(faculty)}
                    disabled={isLoading}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-rose-600 border border-transparent rounded-lg shadow-xs hover:bg-rose-700 transition-all disabled:opacity-50"
                  >
                    <FiTrash2 className="w-3.5 h-3.5" />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Faculty Form Overlay Dialog Pop-up Modal */}
      <Modal
        isOpen={showFacultyForm}
        onClose={() => setShowFacultyForm(false)}
        title={editingFaculty ? 'Modify Instructor Log Records' : 'Register New Instructor Account'}
        size="xl"
      >
        <FacultyForm
          faculty={editingFaculty}
          onSave={handleSaveFaculty}
          onCancel={() => setShowFacultyForm(false)}
          isEditing={!!editingFaculty}
          isLoading={isLoading}
        />
      </Modal>

      {/* Delete Confirmation Overlay Dialog Pop-up Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteFaculty}
        title="Confirm configuration removal"
        message={`Are you completely certain you want to purge ${facultyToDelete?.name || 'this instructor entry'}? This query drops your database references instantly.`}
        confirmText={isLoading ? "Purging..." : "Delete Member"}
        type="danger"
      />

      {/* Feedback Banner Overlay element pop-up system */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};

export default FacultySection;